import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import { type CSSProperties } from "react"

import { type QuickStoryScore } from "#customTypes/composedTypes/standoutTypes"
import {
  quickStoryScoreBand,
  quickStoryScoreTitle,
} from "#utils/quickStoryScore"

import type { QuickStoryScorePhase } from "./useQuickStoryScore"

const CIRCLE = 28

const BAND_COLOR: Record<
  "high" | "mid" | "low" | "muted" | "failed",
  {
    background: string
    color: string
  }
> = {
  high: { background: "#E5F5E6", color: "#1D8921" },
  mid: { background: "#F8E7C0", color: "#8A6A2F" },
  low: { background: "#FDECEC", color: "#A33B3B" },
  muted: { background: "#F2F2F2", color: "#555555" },
  failed: { background: "#FDECEC", color: "#A33B3B" },
}

const circleStyle = (
  tone: keyof typeof BAND_COLOR,
  clickable: boolean
): CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: CIRCLE,
  height: CIRCLE,
  flex: "0 0 auto",
  padding: 0,
  border: "none",
  borderRadius: "50%",
  background: BAND_COLOR[tone].background,
  color: BAND_COLOR[tone].color,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
  cursor: clickable ? "pointer" : "default",
})

/**
 * The confidence that this quick story is safe to auto-approve.
 * The number is the check. A failed check is the same circle with a reload.
 * Nothing here approves the story.
 */
const QuickStoryScoreMark = ({
  phase,
  score,
  note,
  onRetry,
}: {
  phase: Exclude<QuickStoryScorePhase, "hidden">
  score?: QuickStoryScore
  note?: string
  onRetry?: () => void
}) => {
  if (phase === "ready" && score) {
    const band = quickStoryScoreBand(score.confidence)
    return (
      <Tooltip
        title={
          <span style={{ whiteSpace: "pre-line" }}>
            {quickStoryScoreTitle(score, note)}
          </span>
        }
      >
        <span
          data-testid="sr-story-score"
          data-state="ready"
          data-confidence={score.confidence}
          data-positive={score.positive ? "true" : "false"}
          data-appropriate={score.appropriate ? "true" : "false"}
          data-source={score.source}
          data-reason={score.reason}
          style={circleStyle(band, false)}
        >
          {score.confidence}
        </span>
      </Tooltip>
    )
  }

  if (phase === "failed") {
    return (
      <Tooltip title="Retry score">
        <button
          type="button"
          data-testid="sr-story-score"
          data-state="failed"
          aria-label="Retry score"
          onClick={onRetry}
          style={circleStyle("failed", true)}
        >
          <ReloadOutlined />
        </button>
      </Tooltip>
    )
  }

  return (
    <span
      data-testid="sr-story-score"
      data-state="scoring"
      aria-busy="true"
      aria-label="Scoring"
      style={circleStyle("muted", false)}
    >
      <LoadingOutlined />
    </span>
  )
}

export default QuickStoryScoreMark
